import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { Dot } from "../UIElements/svgIcons";
import { headerSty, contentSty } from "../table-styles";
import {
  formatDecimalNumber,
  formatTextType,
  formatTime,
  getUrlPathId,
  limitToSeven,
} from "../../utils/url-id";
import { useAuth } from "../../hooks/useAuth";
import { useMutation } from "@tanstack/react-query";
import { apiGetMedicAppraisal, apiGetMedicIdTimesheet } from "../../api/medic";
import { useNotificationService } from "../notification";
import { MedicAppraisalProp, MedicTimesheetInfo } from "../../interface/medic";


export default function MedicAppraisal() {
  const theme = useTheme();
  const headStyles = { ...headerSty, color: "#93A5D8", fontWeight: 400 };
  const contentStyles = { ...contentSty };
  const location = useLocation();
  const { token } = useAuth();
  const { showNotification } = useNotificationService();
  const [appraisal, setApparisal] = React.useState<MedicAppraisalProp[]>([]);
  React.useEffect(() => {
    const medicId = getUrlPathId(location?.pathname);
    const fields = { medicId: medicId, token: token };
    mutation.mutate(fields);
  }, []);

  const mutation = useMutation({
    mutationFn: (fields: { medicId: string; token: string }) =>
        apiGetMedicAppraisal(fields),
    onSuccess: (data) => {
      
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        setApparisal(data?.data?.data);
      }
    },
    onError: (error) => {
      showNotification("Fetch process failed", "error");
    },
  });
  return (
    <TableContainer
      component={Paper}
      sx={{
        px: 2,
        py: 3,
        width: "100%",
        mx: "auto",
        minHeight: 300,
        background: "#FAFBFC",
        borderRadius: "16px",
        boxShadow: "none",
      }}
    >
      <Table
        sx={{ width: "98%", mx: "auto" }}
        size="small"
        aria-label="a dense table"
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headStyles }}>ID</TableCell>
            <TableCell sx={{ ...headStyles }}>Appraisal Type</TableCell>
            <TableCell sx={{ ...headStyles }}>Performance Rating</TableCell>
            <TableCell sx={{ ...headStyles }}>Strength</TableCell>
            <TableCell sx={{ ...headStyles }}>Comment </TableCell>
            <TableCell sx={{ ...headStyles }}>Overall Summary</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {appraisal.map((row: MedicAppraisalProp) => (
            <TableRow
              key={row._id}
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
            >
              <TableCell
                sx={{ ...contentStyles, textAlign: "left", padding: "12px" }}
                component="th"
                scope="row"
              >
                {limitToSeven(row._id) ?? "-"}
              </TableCell>
              <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                {formatTextType(row?.appraisal_type) ?? "-"}
              </TableCell>
              <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                {row?.performance_rating}
              </TableCell>
              <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                {row?.strengths?.length}
              </TableCell>
              <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                {row?.comment}
              </TableCell>
              <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                {row?.overall_summary ?? "-"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {appraisal?.length === 0 && !mutation.isPending && (
        <Box
          sx={{
            height: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, color: theme.palette.neutral.main }}
          >
            No jobs Available.
          </Typography>
        </Box>
      )}
      {mutation.isPending && (
        <Box sx={{ width: "96%", mx: "auto" }}>
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
        </Box>
      )}
    </TableContainer>
  );
}

const EmpStatus: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        margin: "10px",
        paddingY: 1,
        paddingX: 2,
        borderRadius: "3px",
        color: theme.palette.primary.main,
        textAlign: "center",
        width: 60,
        backgroundColor: theme.palette.lightBlue.main,
      }}
    >
      {children}
    </Box>
  );
};
